/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { FEEDBACK_FILTER_LIMIT } from "components/search/searchBarConstants"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, FilterTrigger } from "constants/tracking"
import { BusinessType, MerchantStoreInfo } from "types/models/menu"
import { actionCreator } from "store/duck/menu"
import { ServiceType, ServiceTypeOptions } from "constants/feedback"
import { MerchantAggregate } from "types/api"

export type FeedbackStoreFilter = {
  search?: string
  city?: number
  businessType?: BusinessType
  serviceType?: ServiceType
}

const useFeedbackStorelist = (
  eventCategory: EventCategory = EventCategory.feedbackHomePage,
) => {
  const dispatch = useDispatch()

  const {
    menu: {
      outletsSearch = [],
      isFetchingOutletsSearch,
      merchantDeliverOptions,
    },
  } = useSelector((state: AppState) => state)

  const filteredList = (() => {
    if (isFetchingOutletsSearch) return []
    return outletsSearch
  })()

  const handleFilter = useCallback(
    async (filterObj: FeedbackStoreFilter) => {
      const getDeliverOption = (value: ServiceType) => {
        if (value === ServiceType.ALL) return undefined

        const deliverOption = merchantDeliverOptions?.map(
          ({ deliverOption }) => deliverOption,
        )

        if (value === ServiceType.DELIVERY)
          return deliverOption?.filter(
            item =>
              item?.includes(ServiceTypeOptions.DELIVERY) ||
              item?.includes(ServiceTypeOptions.TAKEAWAY),
          )

        return deliverOption?.filter(item =>
          item?.includes(ServiceTypeOptions.DINEIN),
        )
      }

      let body: any = {
        search: (filterObj?.search || "").trim(),
        limit: FEEDBACK_FILTER_LIMIT,
      }

      if (eventCategory === EventCategory.feedbackHomePage) {
        body = {
          ...body,
          city: [`${filterObj.city}`],
          businessType: [filterObj.businessType || BusinessType.Food],
          deliverOption: getDeliverOption(
            filterObj.serviceType || ServiceType.ALL,
          ),
        }
      }

      const res: any = await dispatch(actionCreator.searchOutlets(body))
      const merchantStore: MerchantStoreInfo[] =
        res?.payload?.data?.merchants || []

      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.searchStore,
        labels: {
          FILTER_TEXT: filterObj.search,
          FILTER_CITY: filterObj.city,
          FILTER_BUSINESS_TYPE: filterObj.businessType,
          FILTER_DELIVER_OPTION: filterObj.serviceType,
          TRIGGER: FilterTrigger.searchInput,
          RESULT: merchantStore.map(item => item.merchantName).toString(),
          TOTAL_RESULT: merchantStore.length,
        },
      })
    },
    [dispatch, merchantDeliverOptions, eventCategory],
  )

  useEffect(() => {
    dispatch(
      actionCreator.fetchMerchantAggregate(
        MerchantAggregate.cityID,
        MerchantAggregate.businessType,
        MerchantAggregate.deliverOption,
      ),
    )

    dispatch(actionCreator.fetchItemsWithoutPhotoCount())
  }, [dispatch])

  return {
    handleFilter,
    filteredList,
  }
}

export default useFeedbackStorelist
